/**
 * AJAX newsletter / email signup for Shopify {% form 'customer' %}.
 * Same POST flow and marketing signup; no full-page reload.
 */
class PjEmailSignup {
  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    this.submitButton = form.querySelector('[type="submit"]');
    this.defaultSubmitLabel = this.submitButton?.textContent?.trim() || '';
    this.emailInput = form.querySelector('input[type="email"][name="contact[email]"]');
    this.errorStatus = form.querySelector('[data-pj-email-signup-error]');
    this.successStatus = form.querySelector('[data-pj-email-signup-success]');
    this.i18n = this.#readI18n();

    form.dataset.pjEmailSignupReady = 'true';
    form.addEventListener('pj-email-signup-submit', () => this.#onSubmit());
  }

  #readI18n() {
    const source = this.form.querySelector('[data-pj-email-signup-i18n]');
    try {
      return JSON.parse(source?.textContent || '{}');
    } catch {
      return {};
    }
  }

  async #onSubmit() {
    this.#hideStatus(this.errorStatus);
    this.#hideStatus(this.successStatus);

    if (this.emailInput instanceof HTMLInputElement && !this.emailInput.checkValidity()) {
      this.emailInput.reportValidity();
      return;
    }

    this.#setSubmitting(true);

    try {
      await this.#protectWithCaptcha();

      // CAPTCHA token is attached on a real submit event — AJAX without it gets 400.
      if (typeof window.Shopify?.captcha?.protect === 'function') {
        const token = this.form.querySelector('[name="h-captcha-response"]');
        const hasToken =
          (token instanceof HTMLTextAreaElement || token instanceof HTMLInputElement) &&
          Boolean(token.value.trim());
        if (!hasToken) {
          this.form.dataset.pjEmailSignupReady = 'false';
          if (this.submitButton) this.submitButton.disabled = false;
          if (typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit();
          } else {
            this.form.submit();
          }
          return;
        }
      }

      const posted = await this.#postForm();
      if (posted) {
        this.#showSuccess();
        this.form.reset();
        this.successStatus?.focus?.();
      } else {
        this.#showError(this.i18n.error_submit);
      }
    } catch {
      this.#showError(this.i18n.error_submit);
    } finally {
      this.#setSubmitting(false);
    }
  }

  #protectWithCaptcha() {
    const captcha = window.Shopify?.captcha;
    if (!captcha || typeof captcha.protect !== 'function') return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      captcha.protect(this.form, done);
      setTimeout(done, 4000);
    });
  }

  async #postForm() {
    const response = await fetch(this.form.action || '/contact', {
      method: 'POST',
      body: new URLSearchParams(new FormData(this.form)),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
    });

    let posted = false;
    try {
      const params = new URL(response.url).searchParams;
      posted =
        params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
    } catch {
      posted = false;
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const remoteSuccess = doc.querySelector('[data-pj-email-signup-success]:not([hidden])');
    if (remoteSuccess) posted = true;

    const remoteError = doc.querySelector('[data-pj-email-signup-error]:not([hidden])');
    if (!posted && remoteError?.textContent?.trim()) {
      this.#showError(remoteError.textContent.trim());
      return false;
    }

    return posted;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (!this.errorStatus) return;
    this.errorStatus.hidden = false;
    this.errorStatus.textContent = message;
    if (this.emailInput instanceof HTMLInputElement) {
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.setAttribute('aria-describedby', this.errorStatus.id);
    }
  }

  #showSuccess() {
    if (!this.successStatus) return;
    this.successStatus.hidden = false;
    if (this.emailInput instanceof HTMLInputElement) {
      this.emailInput.removeAttribute('aria-invalid');
      this.emailInput.setAttribute('aria-describedby', this.successStatus.id);
    }
  }

  /**
   * @param {HTMLElement | null} status
   */
  #hideStatus(status) {
    if (status) status.hidden = true;
  }

  /**
   * @param {boolean} submitting
   */
  #setSubmitting(submitting) {
    if (!this.submitButton) return;
    this.submitButton.disabled = submitting;
    this.submitButton.textContent = submitting
      ? this.i18n.submitting || this.defaultSubmitLabel
      : this.defaultSubmitLabel;
  }
}

document.querySelectorAll('form.pj-footer__form').forEach((form) => {
  if (!(form instanceof HTMLFormElement)) return;
  if (form.dataset.pjEmailSignupBound === 'true') return;
  form.dataset.pjEmailSignupBound = 'true';

  form.addEventListener(
    'submit',
    (event) => {
      if (form.dataset.pjEmailSignupReady !== 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      form.dispatchEvent(new CustomEvent('pj-email-signup-submit'));
    },
    true
  );

  new PjEmailSignup(form);
});
